import { createFileRoute, Link } from '@tanstack/react-router';
import { useBills } from '../../hooks/useBills';
import { useContacts } from '../../hooks/useContacts';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { formatAccountingDate, formatCurrency } from '../../lib/formatters';
import { billStatusTag } from './bills.index';

export const Route = createFileRoute('/_auth/vendor-center/$id')({
  component: VendorDetailPage,
});

function VendorDetailPage() {
  const { id } = Route.useParams();
  const companyId = useActiveCompanyId();
  const { data: bills = [], isLoading } = useBills(companyId, { vendorId: id });
  const { data: vendors = [] } = useContacts(companyId, { contactType: 'vendor', includeInactive: true });
  const vendor = vendors.find((c) => c.id === id);

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <p className="muted">No active company.</p>
      </div>
    );
  }

  const outstanding = bills
    .filter((b) => b.status !== 'void')
    .reduce((s, b) => s + parseFloat(b.outstandingAmount), 0);
  const pastDue = bills
    .filter((b) => b.isOverdue)
    .reduce((s, b) => s + parseFloat(b.outstandingAmount), 0);

  return (
    <div>
      <Link to="/vendor-center" style={{ fontSize: 13 }}>← Vendor Center</Link>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0 16px', flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>{vendor?.name ?? 'Vendor'}</h1>
        <Link to="/bills/new" className="btn-primary" style={{ textDecoration: 'none' }}>+ New bill</Link>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div className="card" style={{ padding: 16, minWidth: 180 }}>
          <div className="muted" style={{ fontSize: 12 }}>Outstanding</div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{formatCurrency(outstanding.toFixed(2))}</div>
        </div>
        <div className="card" style={{ padding: 16, minWidth: 180 }}>
          <div className="muted" style={{ fontSize: 12 }}>Past due</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: pastDue > 0 ? 'var(--bad)' : undefined }}>
            {formatCurrency(pastDue.toFixed(2))}
          </div>
        </div>
        {vendor?.email && (
          <div className="card" style={{ padding: 16, minWidth: 180 }}>
            <div className="muted" style={{ fontSize: 12 }}>Email</div>
            <div style={{ fontSize: 14 }}>{vendor.email}</div>
          </div>
        )}
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {!isLoading && bills.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <p className="muted" style={{ marginTop: 0 }}>No bills for this vendor yet.</p>
        </div>
      )}

      {bills.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Number</th>
              <th>Ref</th>
              <th>Date</th>
              <th>Due</th>
              <th style={{ textAlign: 'right' }}>Total</th>
              <th style={{ textAlign: 'right' }}>Balance</th>
              <th>Status</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {bills.map((bill) => (
              <tr key={bill.id} style={{ opacity: bill.status === 'void' ? 0.6 : 1 }}>
                <td>{bill.billNumber}</td>
                <td className="muted">{bill.vendorReference ?? '—'}</td>
                <td>{formatAccountingDate(bill.billDate)}</td>
                <td>{formatAccountingDate(bill.dueDate)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(bill.total)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(bill.outstandingAmount)}</td>
                <td>{billStatusTag(bill)}</td>
                <td>
                  <Link to="/bills/$id" params={{ id: bill.id }} className="btn-secondary" style={{ textDecoration: 'none' }}>
                    View
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
